import { useMemo, useState } from 'react';

import { TollOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useFormik } from 'formik';
import { useAccount, useClient } from 'wagmi';
import * as yup from 'yup';

import { rewardTreasuryAbi, useReadRouterRewardTreasury } from '@api/contracts';
import { useWriteSQDTransaction } from '@api/contracts/useWriteTransaction';
import { AccountType, SourceWalletWithBalance } from '@api/subsquid-network-squid';
import { ContractCallDialog } from '@components/ContractCallDialog';
import { Form, FormRow, FormikSelect } from '@components/Form';
import { Loader } from '@components/Loader';
import { SourceWalletOption } from '@components/SourceWallet';
import { useContracts } from '@hooks/network/useContracts';
import { useSquidHeight } from '@hooks/useSquidNetworkHeightHooks';

export const claimSchema = yup.object({
  source: yup.string().label('Source').trim().required('Source is required'),
});

export type SourceWalletWithClaims = SourceWalletWithBalance;

export function ClaimButton({
  sources,
  disabled,
}: {
  sources?: SourceWalletWithClaims[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        startIcon={<TollOutlined />}
        onClick={() => setOpen(true)}
        color="info"
        variant="contained"
        loading={open}
        disabled={disabled}
      >
        CLAIM
      </Button>
      <ClaimDialog sources={sources} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ClaimDialog({
  sources,
  open,
  onClose,
}: {
  sources?: SourceWalletWithClaims[];
  open: boolean;
  onClose: () => void;
}) {
  const client = useClient();
  const account = useAccount();

  const { setWaitHeight } = useSquidHeight();

  const contracts = useContracts();
  const contractWriter = useWriteSQDTransaction({});

  const rewardTreasuryAddress = useReadRouterRewardTreasury({
    address: contracts.ROUTER,
  });

  const isLoading = rewardTreasuryAddress.isLoading;

  const initialValues = useMemo(() => {
    const option = sources?.find(c => c.balance !== '0') || sources?.[0];

    return {
      source: option?.id || '0x',
    };
  }, [sources]);

  const formik = useFormik({
    initialValues,
    validationSchema: claimSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,

    onSubmit: async values => {
      if (!client) return;
      if (!account.address) return;
      if (!rewardTreasuryAddress.data) return;
      if (!sources) return;

      const { source } = claimSchema.cast(values);

      const wallet = sources.find(w => w?.id === source);
      if (!wallet) return;

      const receipt = await contractWriter.writeTransactionAsync({
        address: rewardTreasuryAddress.data,
        abi: rewardTreasuryAbi,
        functionName: 'claimFor',
        args: [contracts.REWARD_DISTRIBUTION, account.address],
        vesting: wallet.type === AccountType.User ? undefined : (wallet.id as `0x${string}`),
      });
      setWaitHeight(receipt.blockNumber, []);

      onClose();
    },
  });

  const hasAvailableClaims = useMemo(() => !!sources?.some(s => s.balance !== '0'), [sources]);

  return (
    <ContractCallDialog
      title="Claim rewards"
      open={open}
      onResult={confirmed => {
        if (!confirmed) return onClose();

        formik.handleSubmit();
      }}
      loading={contractWriter.isPending}
      confirmColor="info"
      disableConfirmButton={!formik.isValid || !hasAvailableClaims}
    >
      {isLoading || !sources ? (
        <Loader />
      ) : (
        <Form onSubmit={formik.handleSubmit}>
          <FormRow>
            <FormikSelect
              id="source"
              showErrorOnlyOfTouched
              options={sources?.map(s => ({
                label: <SourceWalletOption source={s} />,
                value: s.id,
                disabled: s.balance === '0',
              }))}
              formik={formik}
              onChange={e => {
                const source = sources.find(s => s.id === e.target.value);
                if (!source) return;

                formik.setFieldValue('source', source.id);
              }}
            />
          </FormRow>
        </Form>
      )}
    </ContractCallDialog>
  );
}
